// src/components/landing/SystemMap.jsx
import React, { useState } from 'react';
import './SystemMap.css';

const SystemMap = () => {
  const [hoveredNode, setHoveredNode] = useState(null);

  // Radial layout: COURSES at center, all others evenly spaced around
  const centerX = 500;
  const centerY = 300;
  const radius = 285; // Increased from 270 for better spacing with larger text
  
  // Calculate radial positions for outer nodes
  const outerNodes = [
    { id: 'timeline', label: 'TIMELINE', descriptor: 'Deadlines & exams' },
    { id: 'documents', label: 'DOCUMENTS', descriptor: 'Source material' },
    { id: 'flashcards', label: 'FLASHCARDS', descriptor: 'Active recall' },
    { id: 'quizzes', label: 'QUIZZES', descriptor: 'Test prep' },
    { id: 'podcasts', label: 'PODCASTS', descriptor: 'Listen anywhere' },
    { id: 'audiobooks', label: 'AUDIOBOOKS', descriptor: 'Long-form' },
    { id: 'chat', label: 'COURSE CHAT', descriptor: 'Ask in context' },
    { id: 'progress', label: 'PROGRESS', descriptor: 'Levels & tracking' },
  ];
  
  const angleStep = (2 * Math.PI) / outerNodes.length;
  
  const nodes = [
    { id: 'courses', x: centerX, y: centerY, label: 'COURSES', descriptor: 'Your hub', hub: true },
    ...outerNodes.map((node, index) => {
      const angle = index * angleStep - Math.PI / 2; // Start from top
      return {
        ...node,
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
      };
    }),
  ];

  // All outer nodes connect only to COURSES (hub-and-spoke)
  const connections = outerNodes.map(node => ({
    from: 'courses',
    to: node.id,
  }));
  
  const getNodeById = (id) => nodes.find(n => n.id === id);

  const isConnectedToHovered = (nodeId) => {
    if (!hoveredNode) return false;
    return connections.some(
      conn => 
        (conn.from === hoveredNode && conn.to === nodeId) ||
        (conn.to === hoveredNode && conn.from === nodeId)
    );
  };

  return (
    <div className="systemMap">
      <svg viewBox="0 -80 1000 760" className="systemMap__svg">
        <defs>
          {/* Glow filters */}
          <filter id="nodeGlow">
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          
          <filter id="lineGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>

          <filter id="hubGlow">
            <feGaussianBlur stdDeviation="8" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>

          {/* Line gradient */}
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(100, 160, 200, 0.15)" />
            <stop offset="50%" stopColor="rgba(100, 160, 200, 0.35)" />
            <stop offset="100%" stopColor="rgba(100, 160, 200, 0.15)" />
          </linearGradient>
          
          {/* System spine gradient */}
          <linearGradient id="spineGradient" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor="rgba(80, 140, 200, 0)" />
            <stop offset="30%" stopColor="rgba(80, 140, 200, 0.08)" />
            <stop offset="70%" stopColor="rgba(80, 140, 200, 0.08)" />
            <stop offset="100%" stopColor="rgba(80, 140, 200, 0)" />
          </linearGradient>
        </defs>

        {/* System spine - engineered feel */}
        <line
          x1="500"
          y1="100"
          x2="500"
          y2="500"
          stroke="url(#spineGradient)"
          strokeWidth="120"
          opacity="0.6"
          className="systemMap__spine"
        />

        {/* Connection lines - straight radial spokes */}
        <g className="systemMap__connections">
          {connections.map((conn, idx) => {
            const fromNode = getNodeById(conn.from);
            const toNode = getNodeById(conn.to);
            const isHighlighted = hoveredNode === conn.from || hoveredNode === conn.to;
            const isHubLine = conn.from === 'courses' || conn.to === 'courses';
            
            return (
              <line
                key={idx}
                x1={fromNode.x}
                y1={fromNode.y}
                x2={toNode.x}
                y2={toNode.y}
                className={`systemMap__line ${isHighlighted ? 'systemMap__line--highlighted' : ''} ${isHubLine ? 'systemMap__line--hub' : ''}`}
                stroke="rgba(100, 160, 200, 0.7)"
                strokeWidth={isHighlighted ? "3.5" : "2.5"}
              />
            );
          })}
        </g>

        {/* Nodes */}
        <g className="systemMap__nodes">
          {nodes.map((node) => {
            const isHovered = hoveredNode === node.id;
            const isConnected = isConnectedToHovered(node.id);
            const isHub = node.hub;
            
            return (
              <g 
                key={node.id}
                onMouseEnter={() => setHoveredNode(node.id)}
                onMouseLeave={() => setHoveredNode(null)}
                className="systemMap__nodeGroup"
              >
                {/* Outer glow circle */}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={isHub ? 38 : 18}
                  className={`systemMap__nodeGlow ${isHovered || isConnected ? 'systemMap__nodeGlow--active' : ''}`}
                  filter={isHub ? 'url(#hubGlow)' : 'url(#nodeGlow)'}
                />
                
                {/* Inner dot */}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={isHub ? 13 : 7}
                  className={`systemMap__nodeDot ${isHub ? 'systemMap__nodeDot--hub' : ''} ${isHovered || isConnected ? 'systemMap__nodeDot--active' : ''}`}
                />
                
                {/* Label */}
                {node.label && (
                  <>
                    <text
                      x={node.x}
                      y={node.y - (isHub ? 52 : 32)}
                      className={`systemMap__label ${isHub ? 'systemMap__label--hub' : ''} ${isHovered || isConnected ? 'systemMap__label--active' : ''}`}
                      textAnchor="middle"
                    >
                      {node.label}
                    </text>
                    {node.descriptor && (
                      <text
                        x={node.x}
                        y={node.y + (isHub ? 66 : 48)}
                        className={`systemMap__descriptor ${isHub ? 'systemMap__descriptor--hub' : ''} ${isHovered || isConnected ? 'systemMap__descriptor--active' : ''}`}
                        textAnchor="middle"
                      >
                        {node.descriptor}
                      </text>
                    )}
                  </>
                )}
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
};

export default SystemMap;
